/* =============================================
   KOMBI & KLIMA THEME — CORE JS
   ============================================= */

(function () {
  'use strict';

  /* ----- Reduced Motion Check ----- */
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----- Scroll Reveal (IntersectionObserver) ----- */
  function initReveal() {
    const revealEls = document.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    if (!revealEls.length) return;

    if (prefersReducedMotion) {
      revealEls.forEach(el => el.classList.add('revealed'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const delay = el.dataset.revealDelay || 0;
          setTimeout(() => el.classList.add('revealed'), Number(delay));
          observer.unobserve(el);
        }
      });
    }, { threshold: 0.15 });

    revealEls.forEach(el => observer.observe(el));
  }

  /* ----- Stagger Reveal Groups ----- */
  function initStaggerReveal() {
    const groups = document.querySelectorAll('[data-stagger]');
    if (!groups.length) return;

    groups.forEach(group => {
      const children = group.querySelectorAll('.reveal');
      const staggerMs = Number(group.dataset.stagger) || 80;
      children.forEach((child, i) => {
        child.dataset.revealDelay = i * staggerMs;
      });
    });
  }

  /* ----- Ripple Effect on Buttons ----- */
  function initRipple() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.btn');
      if (!btn || prefersReducedMotion) return;

      const ripple = document.createElement('span');
      ripple.classList.add('ripple');
      const rect = btn.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      ripple.style.width = ripple.style.height = size + 'px';
      ripple.style.left = (e.clientX - rect.left - size / 2) + 'px';
      ripple.style.top = (e.clientY - rect.top - size / 2) + 'px';
      btn.appendChild(ripple);
      ripple.addEventListener('animationend', () => ripple.remove());
    });
  }

  /* ----- Back to Top ----- */
  function initBackToTop() {
    const btn = document.getElementById('back-to-top');
    if (!btn) return;

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (window.scrollY > 600) {
            btn.classList.add('visible');
          } else {
            btn.classList.remove('visible');
          }
          ticking = false;
        });
        ticking = true;
      }
    });

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  /* ----- Toast Notification System ----- */
  window.showToast = function (message, type = 'success', duration = 3000) {
    const container = document.getElementById('toast-container');
    if (!container) return;

    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;

    const icons = {
      success: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7 10l2 2 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2"/></svg>',
      error: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2"/><path d="M7 7l6 6M13 7l-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
      info: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2"/><path d="M10 9v4M10 7h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
    };

    toast.innerHTML = `
      <span class="toast-icon">${icons[type] || icons.info}</span>
      <span class="toast-message">${message}</span>
    `;

    container.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('toast-visible'));

    setTimeout(() => {
      toast.classList.remove('toast-visible');
      toast.addEventListener('transitionend', () => toast.remove());
    }, duration);
  };

  /* ----- Header Scroll Effect ----- */
  function initHeaderScroll() {
    const header = document.getElementById('site-header');
    if (!header) return;

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (window.scrollY > 10) {
            header.classList.add('header-scrolled');
          } else {
            header.classList.remove('header-scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  /* ----- Mobile Menu Toggle ----- */
  function initMobileMenu() {
    const toggle = document.getElementById('mobile-menu-toggle');
    const menu = document.getElementById('mobile-menu');
    const overlay = document.getElementById('mobile-menu-overlay');
    if (!toggle || !menu) return;

    function openMenu() {
      menu.classList.add('active');
      if (overlay) overlay.classList.add('active');
      document.body.classList.add('drawer-open');
      toggle.setAttribute('aria-expanded', 'true');
    }

    function closeMenu() {
      menu.classList.remove('active');
      if (overlay) overlay.classList.add('active');
      setTimeout(() => {
        if (overlay) overlay.classList.remove('active');
      }, 10);
      document.body.classList.remove('drawer-open');
      toggle.setAttribute('aria-expanded', 'false');
    }

    toggle.addEventListener('click', () => {
      const isOpen = menu.classList.contains('active');
      isOpen ? closeMenu() : openMenu();
    });

    if (overlay) overlay.addEventListener('click', closeMenu);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('active')) closeMenu();
    });

    /* Accordion submenus */
    menu.querySelectorAll('.mobile-menu-parent').forEach(parent => {
      parent.addEventListener('click', (e) => {
        e.preventDefault();
        const submenu = parent.nextElementSibling;
        if (submenu) {
          parent.classList.toggle('open');
          submenu.classList.toggle('open');
        }
      });
    });
  }

  /* ----- Search Panel ----- */
  function initSearchPanel() {
    const openBtn = document.getElementById('search-open');
    const panel = document.getElementById('search-panel');
    const closeBtn = document.getElementById('search-close');
    const input = document.getElementById('search-input');
    if (!openBtn || !panel) return;

    openBtn.addEventListener('click', () => {
      panel.classList.add('active');
      document.body.classList.add('drawer-open');
      if (input) setTimeout(() => input.focus(), 200);
    });

    function closeSearch() {
      panel.classList.remove('active');
      document.body.classList.remove('drawer-open');
    }

    if (closeBtn) closeBtn.addEventListener('click', closeSearch);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && panel.classList.contains('active')) closeSearch();
    });

    /* Predictive search */
    if (input) {
      let debounceTimer;
      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const query = input.value.trim();
          if (query.length < 2) {
            document.getElementById('search-results').innerHTML = '';
            return;
          }
          fetchPredictiveSearch(query);
        }, 300);
      });
    }
  }

  async function fetchPredictiveSearch(query) {
    const resultsContainer = document.getElementById('search-results');
    if (!resultsContainer) return;

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`
      );
      const data = await response.json();
      const products = data.resources?.results?.products || [];

      if (!products.length) {
        resultsContainer.innerHTML = '<p class="search-no-results">Sonuc bulunamadi</p>';
        return;
      }

      resultsContainer.innerHTML = products.map(product => `
        <a href="${product.url}" class="search-result-item">
          ${product.image ? `<img src="${product.image}" alt="${product.title}" width="60" height="60" loading="lazy">` : ''}
          <div>
            <p class="search-result-title">${product.title}</p>
            <p class="search-result-price">${product.price}</p>
          </div>
        </a>
      `).join('');
    } catch (err) {
      /* silently fail */
    }
  }

  /* ----- Announcement Bar Rotation ----- */
  function initAnnouncementRotation() {
    const bar = document.querySelector('.announcement-bar');
    if (!bar) return;

    const messages = bar.querySelectorAll('.announcement-message');
    if (messages.length <= 1) return;

    let current = 0;
    setInterval(() => {
      messages[current].classList.remove('active');
      current = (current + 1) % messages.length;
      messages[current].classList.add('active');
    }, 5000);
  }

  /* ----- Dropdown Menus ----- */
  function initDropdowns() {
    const dropdowns = document.querySelectorAll('.nav-dropdown-trigger');
    dropdowns.forEach(trigger => {
      const menu = trigger.querySelector('.nav-dropdown');
      if (!menu) return;

      trigger.addEventListener('mouseenter', () => menu.classList.add('open'));
      trigger.addEventListener('mouseleave', () => menu.classList.remove('open'));
      trigger.addEventListener('focusin', () => menu.classList.add('open'));
      trigger.addEventListener('focusout', (e) => {
        if (!trigger.contains(e.relatedTarget)) menu.classList.remove('open');
      });
    });
  }

  /* ----- Init ----- */
  document.addEventListener('DOMContentLoaded', () => {
    initStaggerReveal();
    initReveal();
    initRipple();
    initBackToTop();
    initHeaderScroll();
    initMobileMenu();
    initSearchPanel();
    initAnnouncementRotation();
    initDropdowns();
  });
})();
